import { useMemo, useState } from 'react';
import BizInput from '../../../Core/Components/Mini/BizInput';
import { useGetTimereports } from '../../../Core/Hooks/useTimereport';
import { TimereportItem } from '../../../Core/Types/admin/timereport';
import { formatLocalDate } from '../../../Core/Utils/dateUtils';
import TimereportItemForm from './TimereportItemForm';
import './TimereportList.scss';

const getMonthStart = (): string => {
	const d = new Date();
	return formatLocalDate(new Date(d.getFullYear(), d.getMonth(), 1));
};

const getMonthEnd = (): string => {
	const d = new Date();
	return formatLocalDate(new Date(d.getFullYear(), d.getMonth() + 1, 0));
};

const getStatusText = (item: TimereportItem): string => {
	if (item.isAttested) return 'Attested';
	if (item.isSubmitted) return 'Submitted';
	return 'Draft';
};

const getStatusClass = (item: TimereportItem): string => {
	if (item.isAttested) return 'attested';
	if (item.isSubmitted) return 'submitted';
	return 'draft';
};

const TimereportList = () => {
	const [startDate, setStartDate] = useState(getMonthStart);
	const [endDate, setEndDate] = useState(getMonthEnd);
	const [editingItem, setEditingItem] = useState<TimereportItem | null>(null);

	const { timereports, statusState } = useGetTimereports(startDate, endDate);

	const sortedReports = useMemo(
		() => [...(timereports ?? [])].sort((a, b) => b.date.localeCompare(a.date)),
		[timereports]
	);

	const handleRowClick = (item: TimereportItem) => {
		setEditingItem(item);
	};

	return (
		<div className='biz-timereport-list'>
			<div className='biz-timereport-list__filters'>
				<div className='biz-timereport-list__filter-group'>
					<span className='biz-timereport-list__filter-label'>From</span>
					<BizInput
						type='date'
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
					/>
				</div>
				<div className='biz-timereport-list__filter-group'>
					<span className='biz-timereport-list__filter-label'>To</span>
					<BizInput
						type='date'
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
					/>
				</div>
			</div>

			{statusState === 'LOADING' && <p>Loading...</p>}

			{sortedReports.length === 0 && statusState === 'SUCCESS' && (
				<div className='biz-timereport-list__empty'>No time reports found for the selected period.</div>
			)}

			{sortedReports.length > 0 && (
				<table className='biz-timereport-list__table'>
					<thead>
						<tr>
							<th>Date</th>
							<th>Project</th>
							<th>Activity</th>
							<th>Hours</th>
							<th>Comment</th>
							<th>Status</th>
						</tr>
					</thead>
					<tbody>
						{sortedReports.map((item) => (
							<tr key={item.id} onClick={() => handleRowClick(item)}>
								<td>{item.date.split('T')[0]}</td>
								<td>{item.projectName}</td>
								<td>{item.activityName}</td>
								<td>{item.units}</td>
								<td>{item.comment}</td>
								<td>
									<span className={`biz-timereport-list__status-badge biz-timereport-list__status-badge--${getStatusClass(item)}`}>
										{getStatusText(item)}
									</span>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}

			{editingItem && (
				<TimereportItemForm
					date={editingItem.date.split('T')[0]}
					existingItem={editingItem}
					onClose={() => setEditingItem(null)}
				/>
			)}
		</div>
	);
};

export default TimereportList;
